import baselineRulesJson from './baseline-rules.json';

export type Disposition = 'redact' | 'quarantine';

/** A rule as stored in baseline-rules.json (data; regex is a string). */
export type BaselineRule = {
  id: string;
  description?: string;
  regex: string;
  flags?: string;
  keywords: string[];
  entropy?: number;
  secretGroup: number;
  disposition: Disposition;
  origin: 'gitleaks' | 'lore';
};

/** A rule compiled for the engine: the regex is a ready-to-run matcher. */
export type CompiledRule = {
  id: string;
  keywords: string[];
  entropy?: number;
  secretGroup: number;
  disposition: Disposition;
  origin: 'gitleaks' | 'lore';
  /** Compiled with the global + hasIndices flags so the engine can read
   *  per-group spans via `match.indices`. */
  matcher: RegExp;
};

type Ruleset = {
  schemaVersion: number;
  source?: { tool: string; ref: string; generatedFrom: string };
  rules: BaselineRule[];
};

/** Provenance of the bundled baseline ruleset, for `lore status`. */
export function baselineRulesetInfo(): {
  schemaVersion: number;
  source?: { tool: string; ref: string; generatedFrom: string };
} {
  const ruleset = baselineRulesJson as Ruleset;
  return { schemaVersion: ruleset.schemaVersion, source: ruleset.source };
}

let cached: CompiledRule[] | null = null;

/**
 * Compile the bundled baseline ruleset once. The JSON is inlined into the
 * binary at build time (esbuild), so this does no I/O and never touches the
 * network — the guarantee floor (ADR-0005) holds offline.
 */
export function loadBaselineRules(): CompiledRule[] {
  if (cached) return cached;
  const ruleset = baselineRulesJson as Ruleset;
  cached = ruleset.rules.map((rule) => ({
    id: rule.id,
    keywords: rule.keywords,
    ...(rule.entropy != null ? { entropy: rule.entropy } : {}),
    secretGroup: rule.secretGroup,
    disposition: rule.disposition,
    origin: rule.origin,
    matcher: new RegExp(rule.regex, `gd${rule.flags ?? ''}`),
  }));
  return cached;
}
